import type { Map } from '@gonfalon/immutable-shim/immutableShim';
import { fromJS, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import {
  http,
  jsonToImmutable,
  jsonToImmutableError,
  restApitoImmutableError,
} from '@gonfalon/legacy-http-client/httpUtils';
import { ApiVersions } from '@gonfalon/openapi/api-versions';
import type { GetTeamQueryParams } from '@gonfalon/openapi/operations/getTeam';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { getMemberAccessSummaryQuery, getTeamAccessSummaryQuery } from '@gonfalon/rest-api/queries/accessSummaries';
import { getTeamQuery } from '@gonfalon/rest-api/queries/teams';
import { normalize, schema } from 'normalizr';

import { convertMapToOrderedMap } from '#utils/collectionUtils';
import type { Link } from '#utils/linkUtils';
import { Logger } from '#utils/logUtils';
import type { Team } from '#utils/teams/records/Team';
import { createTeam as createTeamRecord } from '#utils/teams/records/Team';
import type { TeamForm } from '#utils/teams/records/TeamForm';
import type { TeamsFilters } from '#utils/teams/records/TeamsFilters';
import type { TeamsSemanticPatchInstructions } from '#utils/teams/types';
import { createTeamsBulkAddMembersResponse } from '#utils/teamsBulkAddMembersUtils';

const logger = Logger.get('TeamsAPI');

const teamsEntity = new schema.Entity(
  'teams',
  {},
  {
    idAttribute: 'key',
  },
);

const url = '/api/v2/teams';

export type teamExpandOptions = 'maintainers' | 'members' | 'projects' | 'roles';

export const getTeams = async (filters: TeamsFilters, expandOptions?: teamExpandOptions[]) => {
  const expandString = expandOptions?.length ? `&expand=${expandOptions?.join(',')}` : '';
  const queryString = `${filters.toBackendQueryString()}${expandString}`;

  const response = await http.get(`${url}${queryString}`, {
    headers: { 'Ld-Api-Version': ApiVersions.V4 },
  });
  const responseJson = await response.json();
  const normalized = normalize(responseJson, { items: [teamsEntity] });
  const immutable = fromJS(normalized);
  return immutable.withMutations((map: Map<string, OrderedMap<string, Team>>) => {
    map.updateIn(['entities', 'teams'], (teams) => convertMapToOrderedMap(teams, normalized.result.items));
    map.updateIn(['entities', 'teams'], (teams) => (teams ? teams.map(createTeamRecord) : OrderedMap()));
  });
};

export type LoadMoreTeamMaintainersArgs = {
  teamKey: string;
  nextPageLink?: Link;
};

export const getTeam = (teamKey: string, query: GetTeamQueryParams = {}) => {
  const { expand = [], ...restQuery } = query;
  const queryClient = getQueryClient();
  return queryClient
    .fetchQuery(
      getTeamQuery({
        teamKey,
        query: { expand: ['maintainers', ...expand], ...restQuery },
        apiVersion: ApiVersions.Beta,
      }),
    )
    .then((data) => fromJS(data))
    .then(createTeamRecord)
    .catch(restApitoImmutableError);
};

export const postTeam = async (team: TeamForm) => {
  try {
    const response = await http
      .post(`${url}?expand=maintainers,roles`, {
        beta: true,
        body: team.toRep(),
      })
      .then(jsonToImmutable, jsonToImmutableError);
    return createTeamRecord(response);
  } catch (e) {
    const err = e as { toJS?: () => unknown } | null | undefined;
    logger.error('createTeam error', typeof err?.toJS === 'function' ? err.toJS() : e);
    throw e;
  }
};

export const patchTeam = async (teamKey: string, instructions: TeamsSemanticPatchInstructions[]) => {
  try {
    const response = await http
      .patch(`${url}/${teamKey}?expand=maintainers,roles`, {
        beta: true,
        body: { instructions },
      })
      .then(async (data) => {
        const queryClient = getQueryClient();
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getTeamQuery.partialQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getMemberAccessSummaryQuery.partialQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getTeamAccessSummaryQuery.partialQueryKey() }),
        ]);
        return data;
      })
      .then(jsonToImmutable, jsonToImmutableError);
    return createTeamRecord(response);
  } catch (e) {
    const err = e as { toJS?: () => unknown } | null | undefined;
    logger.error('patchTeam error', typeof err?.toJS === 'function' ? err.toJS() : e);
    throw e;
  }
};

export const postTeamMembers = (teamKey: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return http
    .post(`${url}/${teamKey}/members`, { beta: true, body: formData })
    .then(jsonToImmutable, jsonToImmutableError)
    .then(createTeamsBulkAddMembersResponse)
    .catch((e) => {
      logger.error('postTeamMembers error', typeof e?.toJS === 'function' ? e.toJS() : e);
      throw e;
    });
};
